import * as React from 'react';
import Button from '@mui/material/Button';
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Pagination,
    Paper,
    Select,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import {
    DestinationFolder,
    getCompetition,
    getCompetitionMatches,
    getCompetitionOptions,
    normalizeCompetitionBackgroundUrl,
    resolveStrapiMediaUrlForDisplay,
    saveCompetition,
    uploadFile,
} from '../Api';
import {useNavigate, useParams} from 'react-router-dom';
import CompetitionDto from "../models/dtos/CompetitionDto";
import CompetitionOptions from "../models/dtos/CompetitionOptions";
import MatchShortInfoDto from "../models/dtos/MatchShortInfo";
import {StrapiFolder, StrapiImage} from "../models/dtos/StrapiFolder";

function normalizeStrapiAuthHeader(token: string): string {
    const t = token.trim()
    if (!t) return t
    if (/^(Bearer|bearer)\s/.test(t)) return t
    return `Bearer ${t}`
}

/** Strapi / custom plugins may return `files` at root or under `data`. */
function filesFromStrapiListResponse(raw: unknown): StrapiImage[] {
    if (raw == null || typeof raw !== 'object') return []
    const o = raw as Record<string, unknown>
    if (Array.isArray(o.files)) return o.files as StrapiImage[]
    if (o.data && typeof o.data === 'object') {
        const d = o.data as Record<string, unknown>
        if (Array.isArray(d.files)) return d.files as StrapiImage[]
    }
    return []
}

const MAX_COMPETITION_MATCHES = 10
const MATCH_BROWSE_PAGE_SIZE = 25
type MatchAddTarget = 'primary' | 'reserve'

function parseMatchIdsInput(value: string): number[] {
    return value
        .split(/[,;\s]+/)
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .map(Number)
        .filter(n => !isNaN(n) && n > 0)
}

const MATCH_SORT_OPTIONS = [
    { id: 'bets', label: 'Most bet (in app)' },
    { id: 'views', label: 'Most viewed' },
    { id: 'interest', label: 'Interest score (league + teams)' },
    { id: 'start_date', label: 'Kick-off soonest' },
] as const

const MATCH_TIME_END_OPTIONS = [
    { id: '6h', label: 'Next 6 hours' },
    { id: '12h', label: 'Next 12 hours' },
    { id: '1d', label: 'Next 24 hours' },
    { id: '3d', label: 'Next 3 days' },
    { id: '7d', label: 'Next 7 days' },
    { id: '14d', label: 'Next 14 days' },
    { id: '30d', label: 'Next 30 days' },
    { id: 'none', label: 'No upper limit' },
] as const

/** datetime-local value (local wall time) → ISO-8601 instant for analytics API (Instant.parse). */
function toIsoFromDatetimeLocal(value: string): string | undefined {
    if (!value?.trim()) return undefined
    const trimmed = value.trim()
    const d = new Date(trimmed.length === 16 ? `${trimmed}:00` : trimmed)
    if (isNaN(d.getTime())) return undefined
    return d.toISOString()
}

function matchSelectionStatus(
    matchId: number,
    primaryIds: number[],
    reserveIds: number[],
): 'primary' | 'reserve' | null {
    if (primaryIds.includes(matchId)) return 'primary'
    if (reserveIds.includes(matchId)) return 'reserve'
    return null
}

export default function AddCompetitionPage() {
    let id = useParams()['id'] as string
    let isEditMode = id != undefined;

    const [state, setState] = React.useState({
        isEditMode: isEditMode, // funnelPresent: false,
        dialogOpen: false,
        dialogMsg: '', // templates: [] as TemplateDto[],
        competitionPresent: false,
        competition: { enterFee: 5000, reserveSportMatchIds: [], sportMatchIds: [], betGroupIds: [] } as unknown as CompetitionDto,
        options: {} as CompetitionOptions,
        matches: [] as MatchShortInfoDto[],
        competitionMatches: [] as MatchShortInfoDto[],
        reserveCompetitionMatches: [] as MatchShortInfoDto[],
        matchAddTarget: 'primary' as MatchAddTarget,
        matchSearchTermIds: '' as string | null,
        matchesLoading: false,
        beginsIn: 'all' as string,
        endsIn: '7d' as string,
        startsAfterLocal: '' as string,
        startsBeforeLocal: '' as string,
        browseActive: false,
        matchSortBy: 'bets' as string,
        matchesBrowsePage: 0,
        matchesTotalItems: 0,
        images: {} as StrapiFolder,
        currentImageName: '' as string | null,
        uploading: false,
        /** Why Strapi image list is empty, or CORS/HTTP error (so it is not silent). */
        strapiListMessage: null as string | null,
    });
    const navigate = useNavigate();

    React.useEffect(() => {

        const fetchData = async () => {
            if (isEditMode) {
                let [competition, options] = await Promise.all([getCompetition(id), getCompetitionOptions()]);
                const normalizedOptions = {
                    ...options, betGroups: options.betGroups, sports: options.sports
                };
                setState(prevState => ({
                    ...prevState,
                    competition: {
                        ...competition,
                        backgroundUrl: normalizeCompetitionBackgroundUrl(competition.backgroundUrl),
                    },
                    options: normalizedOptions as CompetitionOptions,
                    competitionPresent: true,
                }));
                const reserveIds = competition.reserveSportMatchIds ?? []
                const [matches, reserveMatches] = await Promise.all([
                    getCompetitionMatches(competition.betGroupIds, competition.sport?.id ?? null, 10, 0, competition.sportMatchIds, 'all', 'start_date'),
                    reserveIds.length > 0
                        ? getCompetitionMatches(competition.betGroupIds, competition.sport?.id ?? null, reserveIds.length, 0, reserveIds, 'all', 'start_date')
                        : Promise.resolve(null),
                ]);
                setState(prevState => ({
                    ...prevState,
                    competitionMatches: matches?.items ?? [],
                    reserveCompetitionMatches: reserveMatches?.items ?? [],
                }));

            } else {
                let [options] = await Promise.all([getCompetitionOptions()]);
                const normalizedOptions = {
                    ...options, betGroups: options.betGroups,
                };
                setState(prevState => ({
                    ...prevState, options: normalizedOptions as CompetitionOptions, competitionPresent: true,
                }));
            }
            try {
                const images = await fetchCompetitionImagesOptional();
                setState(prevState => ({
                    ...prevState,
                    currentImageName: images.files && images.files.length > 0 ? images.files.find(x =>
                        (normalizeCompetitionBackgroundUrl(x.url) ?? '') === (normalizeCompetitionBackgroundUrl(prevState.competition.backgroundUrl) ?? '')
                    )?.name ?? null : null
                }));
            } catch (e) {
                const reason = e instanceof Error ? e.message : String(e)
                if (reason.toLowerCase().includes('failed to fetch') || reason.includes('NetworkError')) {
                    setState(prev => ({
                        ...prev,
                        strapiListMessage: 'Strapi: request failed (often CORS). The browser must be allowed to call your CMS origin, or use the dev proxy. Upload via “analytics” still works.',
                    }))
                } else {
                    setState(prev => ({ ...prev, strapiListMessage: `Strapi: ${reason}` }))
                }
            }

        };

        fetchData();
    }, [])

    const onSportSelected = (sportId: number) => {
        if (sportId <= 0) return
        let selectedSport = state.options.sports.find(x => x.id === sportId);
        setState(prevState => ({
            ...prevState,
            competition: {
                ...prevState.competition,
                sport: selectedSport ?? {id: sportId, name: '', photo_url: '', background_url: ''}
            }
        }));
        if ((state.competition.betGroupIds?.length ?? 0) > 0) {
            const searchIds = parseMatchIdsInput(state.matchSearchTermIds ?? '')
            void fetchMatches(state.competition.betGroupIds ?? [], searchIds.length > 0 ? searchIds : null, undefined, sportId)
        }
    }
    const onImageSelected = (imageId: number) => {
        let image = state.images.files?.find(x => x.id === imageId)
        let url = image?.url ?? null;
        if (url == null) {
            return
        }
        const normalized = normalizeCompetitionBackgroundUrl(url)
        setState(prevState => ({
            ...prevState,
            competition: {...prevState.competition, backgroundUrl: normalized},
            currentImageName: image?.name ?? null
        }));
    }
    const fetchMatches = async (
        betGroupIds: number[],
        searchId: number[] | null,
        timeOverrides?: { endsIn?: string; startsAfterLocal?: string; startsBeforeLocal?: string },
        sportIdOverride?: number,
        sortOverride?: string,
        page: number = 0,
    ) => {
        const isIdSearch = (searchId?.length ?? 0) > 0
        if (betGroupIds.length === 0) {
            setState(prevState => ({...prevState, matches: [], browseActive: false}));
            return;
        }
        const sportId = sportIdOverride ?? state.competition.sport?.id
        if (!sportId) {
            setState(prevState => ({
                ...prevState,
                matches: [],
                browseActive: false,
                dialogOpen: true,
                dialogMsg: 'Select a sport before searching for matches.',
            }));
            return;
        }
        const endsIn = timeOverrides?.endsIn ?? state.endsIn
        const startsAfterLocal = timeOverrides?.startsAfterLocal ?? state.startsAfterLocal
        const startsBeforeLocal = timeOverrides?.startsBeforeLocal ?? state.startsBeforeLocal
        const sortBy = sortOverride ?? state.matchSortBy
        const rowOffset = page * MATCH_BROWSE_PAGE_SIZE
        setState(prevState => ({...prevState, matchesLoading: true}));
        try {
            const [matchList] = await Promise.all([
                getCompetitionMatches(
                    betGroupIds,
                    sportId ?? null,
                    MATCH_BROWSE_PAGE_SIZE,
                    rowOffset,
                    isIdSearch ? searchId! : [],
                    'all',
                    sortBy,
                    endsIn === 'none' ? null : endsIn,
                    toIsoFromDatetimeLocal(startsAfterLocal),
                    toIsoFromDatetimeLocal(startsBeforeLocal),
                ),
            ]);
            const items = matchList?.items ?? []
            const totalItems = matchList?.totalItems ?? items.length
            setState(prevState => ({
                ...prevState,
                matches: items,
                matchesBrowsePage: page,
                matchesTotalItems: totalItems,
                matchesLoading: false,
                browseActive: true,
                dialogOpen: items.length === 0 && isIdSearch,
                dialogMsg: items.length === 0 && isIdSearch
                    ? 'No matches found for the given ID(s). Check sport, bet group, and that the match has the selected bets available.'
                    : prevState.dialogMsg,
            }));
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Failed to load matches'
            setState(prevState => ({
                ...prevState,
                matches: [],
                matchesTotalItems: 0,
                matchesBrowsePage: 0,
                browseActive: false,
                matchesLoading: false,
                dialogOpen: true,
                dialogMsg: msg,
            }));
        }
    };

    const addMatchToSelection = (match: MatchShortInfoDto) => {
        setState(prevState => {
            const target = prevState.matchAddTarget
            const primaryIds = prevState.competition.sportMatchIds ?? []
            const reserveIds = prevState.competition.reserveSportMatchIds ?? []
            if (primaryIds.includes(match.id) || reserveIds.includes(match.id)) {
                return prevState
            }
            if (target === 'primary') {
                if (primaryIds.length >= MAX_COMPETITION_MATCHES) {
                    return {
                        ...prevState,
                        dialogOpen: true,
                        dialogMsg: `You can select at most ${MAX_COMPETITION_MATCHES} primary matches.`,
                    }
                }
                return {
                    ...prevState,
                    competition: {
                        ...prevState.competition,
                        sportMatchIds: [...primaryIds, match.id],
                    },
                    competitionMatches: prevState.competitionMatches.some(m => m.id === match.id)
                        ? prevState.competitionMatches
                        : [...prevState.competitionMatches, match],
                }
            }
            return {
                ...prevState,
                competition: {
                    ...prevState.competition,
                    reserveSportMatchIds: [...reserveIds, match.id],
                },
                reserveCompetitionMatches: prevState.reserveCompetitionMatches.some(m => m.id === match.id)
                    ? prevState.reserveCompetitionMatches
                    : [...prevState.reserveCompetitionMatches, match],
            }
        })
    }

    const removeMatchFromSelection = (matchId: number, target: MatchAddTarget) => {
        if (target === 'primary') {
            setState(prevState => ({
                ...prevState,
                competition: {
                    ...prevState.competition,
                    sportMatchIds: (prevState.competition.sportMatchIds ?? []).filter(x => x !== matchId),
                },
                competitionMatches: prevState.competitionMatches.filter(m => m.id !== matchId),
            }))
            return
        }
        setState(prevState => ({
            ...prevState,
            competition: {
                ...prevState.competition,
                reserveSportMatchIds: (prevState.competition.reserveSportMatchIds ?? []).filter(x => x !== matchId),
            },
            reserveCompetitionMatches: prevState.reserveCompetitionMatches.filter(m => m.id !== matchId),
        }))
    }

    const addAllAvailableMatches = () => {
        setState(prevState => {
            const target = prevState.matchAddTarget
            const primaryIds = prevState.competition.sportMatchIds ?? []
            const reserveIds = prevState.competition.reserveSportMatchIds ?? []
            const taken = new Set([...primaryIds, ...reserveIds])
            const available = prevState.matches.filter(m => !taken.has(m.id))
            const toAdd = target === 'primary'
                ? available.slice(0, MAX_COMPETITION_MATCHES - primaryIds.length)
                : available
            if (target === 'primary' && primaryIds.length >= MAX_COMPETITION_MATCHES) {
                return {
                    ...prevState,
                    dialogOpen: true,
                    dialogMsg: `Primary list is full (${MAX_COMPETITION_MATCHES} matches). Switch to Reserve to add more.`,
                }
            }
            if (toAdd.length === 0) {
                return {
                    ...prevState,
                    dialogOpen: true,
                    dialogMsg: 'No new matches to add from the current search results.',
                }
            }
            if (target === 'primary') {
                return {
                    ...prevState,
                    competition: {
                        ...prevState.competition,
                        sportMatchIds: [...primaryIds, ...toAdd.map(m => m.id)],
                    },
                    competitionMatches: [
                        ...prevState.competitionMatches,
                        ...toAdd.filter(m => !prevState.competitionMatches.some(cm => cm.id === m.id)),
                    ],
                }
            }
            return {
                ...prevState,
                competition: {
                    ...prevState.competition,
                    reserveSportMatchIds: [...reserveIds, ...toAdd.map(m => m.id)],
                },
                reserveCompetitionMatches: [
                    ...prevState.reserveCompetitionMatches,
                    ...toAdd.filter(m => !prevState.reserveCompetitionMatches.some(cm => cm.id === m.id)),
                ],
            }
        })
    }

    const runMatchSearch = () => {
        const ids = parseMatchIdsInput(state.matchSearchTermIds ?? '')
        void fetchMatches(state.competition.betGroupIds ?? [], ids.length > 0 ? ids : null)
    }
    const selectedCount = state.competition.sportMatchIds?.length ?? 0
    const reserveCount = state.competition.reserveSportMatchIds?.length ?? 0
    const canSearchMatches =
        (state.competition.betGroupIds?.length ?? 0) > 0 && (state.competition.sport?.id ?? 0) > 0
    const primaryIds = state.competition.sportMatchIds ?? []
    const reserveIds = state.competition.reserveSportMatchIds ?? []
    const allSelectedIds = new Set([...primaryIds, ...reserveIds])
    const browseRows = state.matches
    const availableToAdd = browseRows.filter(x => !allSelectedIds.has(x.id))
    const primarySlotsLeft = MAX_COMPETITION_MATCHES - selectedCount
    const canAddToTarget = (target: MatchAddTarget, status: 'primary' | 'reserve' | null) => {
        if (status != null) return false
        return target === 'primary' ? primarySlotsLeft > 0 : true
    }
    const addAllCount = state.matchAddTarget === 'primary'
        ? Math.min(availableToAdd.length, primarySlotsLeft)
        : availableToAdd.length
    const addAllDisabled = state.matchAddTarget === 'primary'
        ? primarySlotsLeft <= 0 || availableToAdd.length === 0
        : availableToAdd.length === 0

    React.useEffect(() => {
        if (selectedCount >= MAX_COMPETITION_MATCHES && state.matchAddTarget === 'primary') {
            setState(prev => (
                prev.matchAddTarget === 'primary'
                    ? { ...prev, matchAddTarget: 'reserve' as MatchAddTarget }
                    : prev
            ))
        }
    }, [selectedCount, state.matchAddTarget])

    const loadMatchesPage = (page: number) => {
        const searchIds = parseMatchIdsInput(state.matchSearchTermIds ?? '')
        void fetchMatches(
            state.competition.betGroupIds ?? [],
            searchIds.length > 0 ? searchIds : null,
            undefined,
            undefined,
            undefined,
            page,
        )
    }

    const matchesTotalPages = Math.max(1, Math.ceil(state.matchesTotalItems / MATCH_BROWSE_PAGE_SIZE))
    const matchesPageDisplay = state.matchesTotalItems > 0 ? state.matchesBrowsePage + 1 : 0
    const matchesRangeStart = state.matchesTotalItems > 0 ? state.matchesBrowsePage * MATCH_BROWSE_PAGE_SIZE + 1 : 0
    const matchesRangeEnd = state.matchesTotalItems > 0
        ? Math.min((state.matchesBrowsePage + 1) * MATCH_BROWSE_PAGE_SIZE, state.matchesTotalItems)
        : 0

    const handleDialogClose = () => {
        setState(prevState => ({ ...prevState, dialogOpen: false }))
    }
    //onCreate
    const onCreateClicked = async () => {
        const bg = normalizeCompetitionBackgroundUrl(state.competition.backgroundUrl)
        if (!state.competition.name || !state.competition.betGroupIds || !state.competition.sportMatchIds || state.competition.betGroupIds.length === 0 || state.competition.sportMatchIds.length !== MAX_COMPETITION_MATCHES || !state.competition.sport?.id || !bg) {
            const parts: string[] = ['Please fill all required fields.']
            if (!state.competition.name) parts.push('Name is required.')
            if (!state.competition.sport?.id) parts.push('Sport must be selected.')
            if (!state.competition.betGroupIds || state.competition.betGroupIds.length === 0) parts.push('At least one bet group must be selected.')
            if (!state.competition.sportMatchIds || state.competition.sportMatchIds.length !== MAX_COMPETITION_MATCHES) {
                parts.push(`Exactly ${MAX_COMPETITION_MATCHES} matches must be selected (currently ${state.competition.sportMatchIds?.length ?? 0}).`)
            }
            if (!bg) parts.push('Background image is required — upload via dashboard (analytics) or paste a full URL / CMS path.')
            setState(prevState => ({
                ...prevState,
                dialogOpen: true,
                dialogMsg: parts.join(' ')
            }));
            return;
        }
        try {
            await saveCompetition({
                ...state.competition,
                backgroundUrl: bg,
                reserveSportMatchIds: state.competition.reserveSportMatchIds ?? [],
            });
            navigate('/competitions');
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Error while saving competition'
            setState(prevState => ({
                ...prevState, dialogOpen: true, dialogMsg: msg
            }));
        }
    }

    /**
     * Lists images from the Strapi file-system plugin, trying multiple folder names
     * in order. Same mechanism used by `SportsPage.tsx` (folder "Sports"); we try
     * the configured folder first, then historical fallbacks so the dropdown keeps
     * working if the CMS folder was renamed. Dashboard upload (analytics API) does
     * not depend on this call.
     */
    async function fetchCompetitionImagesOptional(): Promise<StrapiFolder> {
        const base = (process.env.REACT_APP_STRAPI_API_URL || '').replace(/\/?$/, '')
        const token = process.env.REACT_APP_STRAPI_API_AUTH_TOKEN || ''
        if (!base || !token) {
            const empty: StrapiFolder = { files: [] }
            setState(prevState => ({
                ...prevState,
                images: empty,
                strapiListMessage: 'Strapi list disabled: set REACT_APP_STRAPI_API_URL and REACT_APP_STRAPI_API_AUTH_TOKEN in the dashboard .env and rebuild. Without them, the dropdown cannot load.',
            }))
            return empty
        }

        const configured = (process.env.REACT_APP_STRAPI_COMPETITIONS_FOLDER || '').trim()
        const folderCandidates: string[] = []
        if (configured) folderCandidates.push(configured)
        for (const fallback of ['Competitions', 'API Uploads', 'Sports']) {
            if (!folderCandidates.includes(fallback)) folderCandidates.push(fallback)
        }

        const attempts: Array<{ folder: string; status: number; body: string }> = []
        for (const docName of folderCandidates) {
            const url = `${base}/file-system/docs/${encodeURIComponent(docName)}`
            try {
                const res = await fetch(url, {
                    headers: {
                        'Authorization': normalizeStrapiAuthHeader(token),
                    },
                })
                if (!res.ok) {
                    const errBody = (await res.text().catch(() => '')).trim().slice(0, 300)
                    attempts.push({ folder: docName, status: res.status, body: errBody })
                    continue
                }
                const raw: unknown = await res.json()
                const parsed = filesFromStrapiListResponse(raw)
                const rawFiles = (raw as StrapiFolder)?.files
                const finalFiles: StrapiImage[] =
                    parsed.length > 0
                        ? parsed
                        : Array.isArray(rawFiles)
                          ? (rawFiles as StrapiImage[])
                          : []
                const out: StrapiFolder = { files: finalFiles }
                setState(prevState => ({
                    ...prevState,
                    images: out,
                    strapiListMessage:
                        finalFiles.length > 0
                            ? null
                            : `Strapi: folder "${docName}" exists but has no files yet. Add images in the CMS to that media folder, or set REACT_APP_STRAPI_COMPETITIONS_FOLDER to the folder name you actually use.`,
                }))
                return out
            } catch (e) {
                const reason = e instanceof Error ? e.message : String(e)
                attempts.push({ folder: docName, status: 0, body: reason })
            }
        }

        const empty: StrapiFolder = { files: [] }
        const summary = attempts
            .map(a => `"${a.folder}" -> ${a.status === 0 ? 'network/CORS' : a.status}`)
            .join(', ')
        setState(prevState => ({
            ...prevState,
            images: empty,
            strapiListMessage:
                `Strapi: no competition image folder found. Tried ${summary}. ` +
                `Create a media folder in the CMS (e.g. "Competitions" — same convention as "Sports") ` +
                `or set REACT_APP_STRAPI_COMPETITIONS_FOLDER to the exact folder name. ` +
                `Upload via "Upload image (analytics API)" still works.`,
        }))
        return empty
    }

    async function uploadCompetitionBackground() {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e: any) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setState(prevState => ({ ...prevState, uploading: true }));
            try {
                const path = await uploadFile(file, DestinationFolder.Ads);
                if (!path) {
                    setState(prevState => ({
                        ...prevState,
                        uploading: false,
                        dialogOpen: true,
                        dialogMsg: 'Upload failed. Check network, login, and that the analytics → main API upload is configured.',
                    }));
                    return;
                }
                const normalized = normalizeCompetitionBackgroundUrl(path);
                setState(prevState => ({
                    ...prevState,
                    uploading: false,
                    competition: {
                        ...prevState.competition,
                        backgroundUrl: normalized,
                    },
                    currentImageName: file.name,
                }));
            } catch (err) {
                const msg = err instanceof Error ? err.message : 'Upload failed';
                setState(prevState => ({
                    ...prevState,
                    uploading: false,
                    dialogOpen: true,
                    dialogMsg: msg,
                }));
            }
        };
        input.click();
    }

    return (

        <div>
            <Stack gap="50px 50px" sx={{
                mx: 5, my: 5
            }

            } justifyContent='center' alignItems='center'>

                <Grid container spacing={2} columns={{xs: 1, sm: 2, md: 4, lg: 6}} alignItems='center'>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2}>
                        <Typography variant="h3">{isEditMode ? "Edit competition" : "Add new competition"}</Typography>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2}>
                        <Typography variant="h6">Competition configuration:</Typography>
                    </Grid>
                    {!state.competitionPresent && state.isEditMode ? <React.Fragment>
                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                        <Grid item xs={2} key="progress"><CircularProgress size={64}
                                                                           style={{marginBottom: '32px'}}/></Grid>
                    </React.Fragment> : <React.Fragment>

                        {/*region ID*/}
                        <Box width="100%"/>
                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                        <Grid item xs={2} key="id">
                            <TextField label="id" variant="filled" value={state.competition.id || ''} fullWidth={true}
                                       disabled={true}/>
                        </Grid>
                        {/*endregion*/}

                        {/*region NAME*/}
                        <Box width="100%"/>
                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                        <Grid item xs={2} key="name">
                            <TextField label="name" variant="filled" value={state.competition.name || ''}
                                       fullWidth={true}
                                       onChange={e => {
                                           setState(prevState => ({
                                               ...prevState,
                                               competition: {...prevState.competition, name: e.target.value}
                                           }));
                                       }}/>
                        </Grid>
                        {/*endregion*/}

                        {/*region IMAGE*/}

                        <Box width="100%"/>
                        <Grid item xs={0} sm={0} md={1} lg={2}/>

                        <Grid item xs={2} key="competition-bg">
                            <Button variant="contained" fullWidth={true} style={{height: '54px', marginTop: '8px'}}
                                    disabled={state.uploading}
                                    onClick={() => {
                                        void uploadCompetitionBackground()
                                    }}>{state.uploading ? 'Uploading…' : 'Upload image (analytics API)'}</Button>

                            <TextField
                                label="Background image URL or path (optional)"
                                variant="filled"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={state.competition.backgroundUrl ?? ''}
                                placeholder="https://… or /uploads/…"
                                onChange={e => {
                                    const v = e.target.value.trim()
                                    setState(prevState => ({
                                        ...prevState,
                                        competition: {
                                            ...prevState.competition,
                                            backgroundUrl: v.length > 0 ? v : null,
                                        },
                                    }))
                                }}
                                helperText="Paste a full URL from admin upload, or a CMS-relative path; the app resolves both."
                            />

                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel id="demo-simple-select-label">{"Strapi gallery (optional)"}</InputLabel>
                                {<Select
                                    fullWidth={true}
                                    labelId="banner-simple-select-label"
                                    id="competition-bg-image-select"
                                    value={state.images.files?.find(x =>
                                        (normalizeCompetitionBackgroundUrl(x.url) ?? '') === (normalizeCompetitionBackgroundUrl(state.competition?.backgroundUrl) ?? '')
                                    )?.id ?? 0}
                                    onChange={e => {
                                        const v = parseInt(e.target.value as string, 10)
                                        if (v === 0) {
                                            setState(prev => ({
                                                ...prev,
                                                competition: {...prev.competition, backgroundUrl: null},
                                                currentImageName: null,
                                            }))
                                        } else {
                                            onImageSelected(v)
                                        }
                                    }}
                                    label="Strapi gallery (optional)">
                                    <MenuItem value={0}><em>— None / use upload above —</em></MenuItem>
                                    {(state.images.files ?? []).map(x => <MenuItem key={x.id}
                                                                                   value={x.id}>{x.name}</MenuItem>)}
                                </Select>}
                            </FormControl>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Recommended: upload here (same flow as Home match ads). Strapi list is optional if credentials exist.
                                The dropdown tries the folder set by <code>REACT_APP_STRAPI_COMPETITIONS_FOLDER</code>,
                                then falls back to <code>Competitions</code> → <code>API Uploads</code> → <code>Sports</code>
                                (same <code>file-system/docs/…</code> plugin used by the Sports page).
                            </Typography>
                            {state.strapiListMessage ? (
                                <Alert
                                    severity={
                                        state.strapiListMessage.startsWith('Strapi: folder') &&
                                        state.strapiListMessage.includes('no files')
                                            ? 'info'
                                            : 'warning'
                                    }
                                    sx={{ mt: 2, whiteSpace: 'pre-wrap' }}
                                >
                                    {state.strapiListMessage}
                                </Alert>
                            ) : null}
                            {resolveStrapiMediaUrlForDisplay(state.competition.backgroundUrl) ? (
                                <Box sx={{ mt: 2, maxWidth: 420 }}>
                                    <Typography variant="subtitle2">Preview</Typography>
                                    <Box
                                        component="img"
                                        src={resolveStrapiMediaUrlForDisplay(state.competition.backgroundUrl)!}
                                        alt=""
                                        sx={{
                                            mt: 1,
                                            width: '100%',
                                            maxHeight: 220,
                                            objectFit: 'contain',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                        }}
                                    />
                                </Box>
                            ) : null}

                        </Grid>
                        {/*endregion*/}

                        {/*region SPORT*/}
                        <Box width="100%"/>
                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                        <Grid item xs={2} key="sport">
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">{"Select Sport"}</InputLabel>
                                {<Select
                                    fullWidth={true}
                                    labelId="banner-simple-select-label"
                                    id="pur-sport-simple-select"
                                    value={state.competition?.sport?.id || 0}
                                    onChange={e => onSportSelected(parseInt(e.target.value as string))}
                                    label="Sport">
                                    <MenuItem value={0} disabled><em>Select sport</em></MenuItem>
                                    {(state.options.sports ?? []).map(x => <MenuItem key={x.id}
                                                                                     value={x.id}>{x.name}</MenuItem>)}
                                </Select>}
                            </FormControl>
                        </Grid>
                        {/*endregion*/}

                        {/*region BET GROUPS ID*/}
                        <Box width="100%"/>
                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                        <Grid item xs={2} key="groups">
                            <FormControl fullWidth>
                                <InputLabel id="betgroup-multiselect-label">Bet groups</InputLabel>
                                <Select
                                    labelId="betgroup-multiselect-label"
                                    id="betgroup-multiselect"
                                    multiple
                                    value={state.competition.betGroupIds || []}
                                    onChange={e => {
                                        const value = e.target.value;
                                        const betGroupIds = typeof value === 'string' ? value.split(',').map(Number) : value as number[];
                                        setState(prevState => ({
                                            ...prevState, competition: {
                                                ...prevState.competition, betGroupIds
                                            }
                                        }));
                                        if ((state.competition.sport?.id ?? 0) > 0) {
                                            void fetchMatches(betGroupIds, null);
                                        }
                                    }}
                                    input={<OutlinedInput label="Bet groups"/>}
                                    renderValue={(selected) => (selected as number[])
                                        .map(id => state.options.betGroups.find(x => x.id === id)?.name)
                                        .join(', ')}>
                                    {(state.options.betGroups ?? []).map(x => (<MenuItem key={x.id} value={x.id}>
                                        <Checkbox checked={state.competition.betGroupIds?.includes(x.id) ?? false}/>
                                        <ListItemText primary={x.name}/>
                                    </MenuItem>))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {/*endregion*/}

                        {/*region TIME WINDOW*/}
                        <Box width="100%"/>
                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                        <Grid item xs={2} key="endsIn">
                            <FormControl fullWidth>
                                <InputLabel id="ends-in-label">Kick-off until</InputLabel>
                                <Select
                                    fullWidth
                                    labelId="ends-in-label"
                                    id="ends-in-select"
                                    value={state.endsIn}
                                    label="Kick-off until"
                                    onChange={e => {
                                        const endsIn = e.target.value as string
                                        setState(prev => ({ ...prev, endsIn }))
                                        if (canSearchMatches && state.browseActive) {
                                            void fetchMatches(
                                                state.competition.betGroupIds ?? [],
                                                null,
                                                { endsIn },
                                            )
                                        }
                                    }}
                                >
                                    {MATCH_TIME_END_OPTIONS.map(x => (
                                        <MenuItem key={x.id} value={x.id}>{x.label}</MenuItem>
                                    ))}
                                </Select>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Matches must start after now and before this window (unless exact dates below).
                                </Typography>
                            </FormControl>
                        </Grid>
                        <Box width="100%"/>
                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                        <Grid item xs={2} key="startsRange">
                            <Stack spacing={2}>
                                <TextField
                                    label="Kick-off from (optional)"
                                    type="datetime-local"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={state.startsAfterLocal}
                                    onChange={e => setState(prev => ({ ...prev, startsAfterLocal: e.target.value }))}
                                />
                                <TextField
                                    label="Kick-off until exact (optional)"
                                    type="datetime-local"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={state.startsBeforeLocal}
                                    onChange={e => setState(prev => ({ ...prev, startsBeforeLocal: e.target.value }))}
                                />
                                <Button
                                    variant="outlined"
                                    disabled={!canSearchMatches}
                                    onClick={() => {
                                        const searchIds = parseMatchIdsInput(state.matchSearchTermIds ?? '')
                                        void fetchMatches(
                                            state.competition.betGroupIds ?? [],
                                            searchIds.length > 0 ? searchIds : null,
                                        )
                                    }}
                                >
                                    Apply time window & refresh
                                </Button>
                            </Stack>
                        </Grid>
                        {/*endregion*/}

                        {/*region MATCH SORT*/}
                        <Box width="100%"/>
                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                        <Grid item xs={2} key="matchSort">
                            <FormControl fullWidth>
                                <InputLabel id="match-sort-label">Sort matches by</InputLabel>
                                <Select
                                    fullWidth
                                    labelId="match-sort-label"
                                    id="match-sort-select"
                                    value={state.matchSortBy}
                                    label="Sort matches by"
                                    onChange={e => {
                                        const sortBy = e.target.value as string
                                        setState(prevState => ({ ...prevState, matchSortBy: sortBy }))
                                        if (canSearchMatches) {
                                            const searchIds = parseMatchIdsInput(state.matchSearchTermIds ?? '')
                                            void fetchMatches(
                                                state.competition.betGroupIds ?? [],
                                                searchIds.length > 0 ? searchIds : null,
                                                undefined,
                                                undefined,
                                                sortBy,
                                            )
                                        }
                                    }}
                                >
                                    {MATCH_SORT_OPTIONS.map(x => (
                                        <MenuItem key={x.id} value={x.id}>{x.label}</MenuItem>
                                    ))}
                                </Select>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Bets = pariuri în app. Interest = ligă + echipe (editorial). Folosit și la competițiile auto.
                                </Typography>
                            </FormControl>
                        </Grid>
                        {/*endregion*/}

                        {/*region MATCH SEARCH TERM ID*/}
                        <Box width="100%"/>
                        <Grid item xs={0} sm={0} md={1} lg={1}/>
                        <Grid item xs={1} sm={2} md={4} lg={4} key="matches">
                            <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
                                <Stack spacing={2}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Typography variant="h6">Matches</Typography>
                                        <Chip
                                            size="small"
                                            color={selectedCount === MAX_COMPETITION_MATCHES ? 'success' : 'warning'}
                                            label={`Primary ${selectedCount}/${MAX_COMPETITION_MATCHES}`}
                                        />
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={reserveCount > 0 ? `Reserve ${reserveCount}` : 'Reserve optional'}
                                        />
                                    </Stack>

                                    <Alert severity="info">
                                        Pick exactly {MAX_COMPETITION_MATCHES} primary matches. Add any number of reserve
                                        matches — they replace a primary at show time only if it has no bets for the selected groups.
                                    </Alert>

                                    {!canSearchMatches ? (
                                        <Alert severity="warning">
                                            Select sport and at least one bet group before searching matches.
                                        </Alert>
                                    ) : null}

                                    <Stack direction="row" spacing={1}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            disabled={!canSearchMatches}
                                            placeholder="Match ID(s): 22985813, 22987611"
                                            value={state.matchSearchTermIds || ''}
                                            onChange={e => {
                                                setState(prevState => ({
                                                    ...prevState, matchSearchTermIds: e.target.value
                                                }));
                                            }}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault()
                                                    runMatchSearch()
                                                }
                                            }}
                                        />
                                        <Button variant="contained" disabled={!canSearchMatches} onClick={() => runMatchSearch()}>
                                            Search
                                        </Button>
                                        <Button variant="outlined" disabled={!canSearchMatches} onClick={() => {
                                            setState(prev => ({ ...prev, matchSearchTermIds: '' }))
                                            void fetchMatches(state.competition.betGroupIds ?? [], null)
                                        }}>
                                            Browse
                                        </Button>
                                    </Stack>

                                    {state.matchesLoading ? (
                                        <Box display="flex" justifyContent="center" alignItems="center" minHeight={64}>
                                            <CircularProgress size={32}/>
                                        </Box>
                                    ) : (state.browseActive || state.matchesLoading) ? (
                                        <Box>
                                            <ToggleButtonGroup
                                                exclusive
                                                size="small"
                                                value={state.matchAddTarget}
                                                onChange={(_, value: MatchAddTarget | null) => {
                                                    if (value) {
                                                        setState(prev => ({ ...prev, matchAddTarget: value }))
                                                    }
                                                }}
                                                sx={{ mb: 1 }}
                                            >
                                                <ToggleButton value="primary" disabled={primarySlotsLeft <= 0 && state.matchAddTarget !== 'primary'}>
                                                    Add to primary ({selectedCount}/{MAX_COMPETITION_MATCHES})
                                                </ToggleButton>
                                                <ToggleButton value="reserve">
                                                    Add to reserve ({reserveCount})
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                            {selectedCount >= MAX_COMPETITION_MATCHES && state.matchAddTarget === 'reserve' ? (
                                                <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 1 }}>
                                                    Primary is complete — new picks go to reserve.
                                                </Typography>
                                            ) : null}
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                                <Typography variant="subtitle2">
                                                    Results ({browseRows.length} on page
                                                    {state.matchesTotalItems > 0 ? ` · ${state.matchesTotalItems} total` : ''})
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={addAllDisabled}
                                                    onClick={() => addAllAvailableMatches()}
                                                >
                                                    Add all new on page ({addAllCount})
                                                </Button>
                                            </Stack>
                                            {state.matchesTotalItems > MATCH_BROWSE_PAGE_SIZE ? (
                                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Showing {matchesRangeStart}–{matchesRangeEnd} of {state.matchesTotalItems}
                                                    </Typography>
                                                    <Pagination
                                                        size="small"
                                                        page={matchesPageDisplay}
                                                        count={matchesTotalPages}
                                                        onChange={(_, p) => loadMatchesPage(p - 1)}
                                                        disabled={state.matchesLoading}
                                                    />
                                                </Stack>
                                            ) : state.matchesTotalItems > 0 ? (
                                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                                    {state.matchesTotalItems} match{state.matchesTotalItems === 1 ? '' : 'es'} found
                                                </Typography>
                                            ) : null}
                                            {browseRows.length > 0 ? (
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>ID</TableCell>
                                                            <TableCell>Home</TableCell>
                                                            <TableCell>Away</TableCell>
                                                            <TableCell>Bets</TableCell>
                                                            <TableCell>Date</TableCell>
                                                            <TableCell>Status</TableCell>
                                                            <TableCell/>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {browseRows.map(match => {
                                                            const status = matchSelectionStatus(match.id, primaryIds, reserveIds)
                                                            const canAdd = canAddToTarget(state.matchAddTarget, status)
                                                            return (
                                                                <TableRow key={`available-${match.id}`}>
                                                                    <TableCell>{match.id}</TableCell>
                                                                    <TableCell>{match.home_team?.name}</TableCell>
                                                                    <TableCell>{match.away_team?.name}</TableCell>
                                                                    <TableCell>{match.bets ?? 0}</TableCell>
                                                                    <TableCell>
                                                                        {match.date ? new Date(match.date).toLocaleString() : ''}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {status === 'primary' ? 'Primary' : status === 'reserve' ? 'Reserve' : '—'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Button
                                                                            size="small"
                                                                            disabled={!canAdd}
                                                                            onClick={() => addMatchToSelection(match)}
                                                                        >
                                                                            {state.matchAddTarget === 'reserve' ? 'Add reserve' : 'Add primary'}
                                                                        </Button>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No matches in this window. Widen “Kick-off until” or adjust exact dates.
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : canSearchMatches ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Use Search or Browse to find matches. You can keep searching while primary and reserve lists are filled below.
                                        </Typography>
                                    ) : null}

                                    {(state.competitionMatches?.length ?? 0) > 0 ? (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                Primary selected ({selectedCount}/{MAX_COMPETITION_MATCHES})
                                            </Typography>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>ID</TableCell>
                                                        <TableCell>Home</TableCell>
                                                        <TableCell>Away</TableCell>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell/>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {state.competitionMatches.map(match => (
                                                        <TableRow key={`selected-${match.id}`}>
                                                            <TableCell>{match.id}</TableCell>
                                                            <TableCell>{match.home_team?.name}</TableCell>
                                                            <TableCell>{match.away_team?.name}</TableCell>
                                                            <TableCell>
                                                                {match.date ? new Date(match.date).toLocaleString() : ''}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Button size="small" onClick={() => removeMatchFromSelection(match.id, 'primary')}>
                                                                    Remove
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    ) : null}

                                    {(state.reserveCompetitionMatches?.length ?? 0) > 0 ? (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                Reserve selected ({reserveCount})
                                            </Typography>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>ID</TableCell>
                                                        <TableCell>Home</TableCell>
                                                        <TableCell>Away</TableCell>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell/>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {state.reserveCompetitionMatches.map(match => (
                                                        <TableRow key={`reserve-${match.id}`}>
                                                            <TableCell>{match.id}</TableCell>
                                                            <TableCell>{match.home_team?.name}</TableCell>
                                                            <TableCell>{match.away_team?.name}</TableCell>
                                                            <TableCell>
                                                                {match.date ? new Date(match.date).toLocaleString() : ''}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Button size="small" onClick={() => removeMatchFromSelection(match.id, 'reserve')}>
                                                                    Remove
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    ) : null}
                                </Stack>
                            </Paper>
                        </Grid>
                        {/*endregion*/}

                        {/*region ENTER FEE*/}
                        <Box width="100%"/>

                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                        <Grid item xs={2} key="enterFee">
                            <TextField label="enterFee" variant="filled" value={state.competition.enterFee || ''}
                                       fullWidth={true}
                                       onChange={e => {
                                           setState(prevState => ({
                                               ...prevState,
                                               competition: {...prevState.competition, enterFee: Number(e.target.value)}
                                           }));
                                       }}/>
                        </Grid>
                        {/*endregion*/}

                        {/*region JACKPOT*/}
                        <Box width="100%"/>
                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                        <Grid item xs={2} key="jackpot">
                            <TextField label="jackpot" variant="filled" value={state.competition.jackpot || ''}
                                       fullWidth={true}
                                       onChange={e => {
                                           setState(prevState => ({
                                               ...prevState,
                                               competition: {...prevState.competition, jackpot: Number(e.target.value)}
                                           }));
                                       }}/>
                        </Grid>
                        {/*endregion*/}

                        {/*region ADD MATCH CONTAINER*/}
                        <Box width="100%"/>
                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                        <Grid item xs={isEditMode ? 1 : 2} key="addMatchContainer">
                            <Button variant="contained" fullWidth={true} style={{height: '54px'}}
                                    disabled={selectedCount !== MAX_COMPETITION_MATCHES}
                                    onClick={() => {
                                        onCreateClicked()
                                    }}>{isEditMode ? "Update competition" : "Add competition"}</Button>
                            {selectedCount !== MAX_COMPETITION_MATCHES ? (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Select exactly {MAX_COMPETITION_MATCHES} matches to enable save.
                                </Typography>
                            ) : null}
                        </Grid>
                        {/*endregion*/}
                    </React.Fragment>}
                </Grid>

            </Stack>
            <Dialog
                open={state.dialogOpen}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    Error
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {state.dialogMsg}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Got it</Button>
                </DialogActions>
            </Dialog>
        </div>)

}